import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import type { StoreProduct } from './api'

export interface CartLine {
  productId: string
  nameAr: string
  unitPrice: number
  unit: string
  quantity: number
  image: string | null
}

interface CartContextValue {
  lines: CartLine[]
  count: number
  subtotal: number
  add: (product: StoreProduct, quantity?: number) => void
  setQuantity: (productId: string, quantity: number) => void
  remove: (productId: string) => void
  clear: () => void
}

const CartContext = createContext<CartContextValue | null>(null)
const STORAGE_KEY = 'najah.cart'

/**
 * السلة محليّة بالكامل: لا تُرسَل للخادم حتى لحظة إتمام الطلب.
 * تبقى بين الجلسات، فلا يفقدها الزبون إن أغلق التطبيق قبل الشراء.
 */
export function CartProvider({ children }: { children: ReactNode }) {
  const [lines, setLines] = useState<CartLine[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      return raw ? (JSON.parse(raw) as CartLine[]) : []
    } catch {
      return []
    }
  })

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(lines))
    } catch {
      // تخزين معطّل — السلة تعيش مع الجلسة فقط
    }
  }, [lines])

  const add = useCallback((product: StoreProduct, quantity = 1) => {
    setLines((prev) => {
      const existing = prev.find((l) => l.productId === product.id)
      if (existing) {
        return prev.map((l) => (l.productId === product.id ? { ...l, quantity: l.quantity + quantity } : l))
      }
      return [
        ...prev,
        {
          productId: product.id,
          nameAr: product.nameAr,
          unitPrice: product.retailPrice,
          unit: product.unit,
          quantity,
          image: product.images[0] ?? null,
        },
      ]
    })
  }, [])

  const setQuantity = useCallback((productId: string, quantity: number) => {
    setLines((prev) =>
      quantity <= 0
        ? prev.filter((l) => l.productId !== productId)
        : prev.map((l) => (l.productId === productId ? { ...l, quantity } : l)),
    )
  }, [])

  const remove = useCallback((productId: string) => {
    setLines((prev) => prev.filter((l) => l.productId !== productId))
  }, [])

  const clear = useCallback(() => setLines([]), [])

  const value = useMemo<CartContextValue>(
    () => ({
      lines,
      count: lines.reduce((sum, l) => sum + l.quantity, 0),
      subtotal: lines.reduce((sum, l) => sum + l.unitPrice * l.quantity, 0),
      add,
      setQuantity,
      remove,
      clear,
    }),
    [lines, add, setQuantity, remove, clear],
  )

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>
}

export function useCart() {
  const ctx = useContext(CartContext)
  if (!ctx) throw new Error('useCart يجب أن يُستعمل داخل CartProvider')
  return ctx
}
